import type { DataSourceEntity } from '@unifyapps/defs/types/dataSource';
import { ENTIRE_ENTITY } from '../../dependency/const';
import type { DataSourceMetaType, QueryRequestResult } from '../../stores/GlobalStateStore';
import { useDataSourceRecords } from '../../stores/DataSourceRecordStore';
import type { ComputeContextType } from '../computeContext/BuildComputeContext';
import useDynamicMemo from '../useDynamicMemo';
import { ComputeContextHelper } from '../computeContext/ComputeContextHelper';
import { ComputeContextKeys } from '../computeContext/const';
import { DependencyPaths } from '../../helper/DependencyPaths';
import { DependencyHelper } from '../../../dependency/helpers/DependencyHelper';

export type DataSourceResponseType = {
  data?: QueryRequestResult;
  meta?: DataSourceMetaType;
  info?: DataSourceEntity;
};

export type DataSourceResponsesType = ReturnType<
  typeof useDataSourceResponses
>['dataSourceResponses'];

export const getDataSourceResponsesDeps = (dataSourceIds: string[]) => {
  const dsDeps = dataSourceIds.map((dsId) => {
    return {
      id: dsId,
      p: [ENTIRE_ENTITY],
    };
  });

  const dsMetaDeps = [
    {
      id: ComputeContextKeys.DATA_SOURCES_META,
      p: dataSourceIds.map((dsId) => {
        const path = DependencyPaths.dataSource(dsId);
        return path.dataSourceMeta;
      }),
    },
  ];

  return DependencyHelper.mergeDependsOn([dsDeps, dsMetaDeps]);
};

function useDataSourceResponses(computeContext: ComputeContextType, dataSourceIds?: string[]) {
  const dataSourcesRecords = useDataSourceRecords();

  const dataSources =
    dataSourceIds?.map((dataSourceId) =>
      ComputeContextHelper.getDataSourceQueryResult(computeContext, dataSourceId),
    ) ?? [];

  const dataSourcesMeta =
    dataSourceIds?.map((dataSourceId) => {
      return ComputeContextHelper.getDataSourceMeta(computeContext, dataSourceId);
    }) ?? [];

  const data = useDynamicMemo(() => {
    return dataSourceIds?.reduce<Record<string, DataSourceResponseType>>((acc, dataSourceId) => {
      const dataSourceInfo = dataSourcesRecords[dataSourceId];

      acc[dataSourceId] = {
        data: ComputeContextHelper.getDataSourceQueryResult(computeContext, dataSourceId),
        meta: ComputeContextHelper.getDataSourceMeta(computeContext, dataSourceId),
        info: dataSourceInfo,
      };

      return acc;
    }, {});
  }, [...dataSources, ...dataSourcesMeta, dataSourceIds, dataSourcesRecords]);

  return { dataSourceResponses: data };
}

export default useDataSourceResponses;
export type UseDataSourceResponsesType = ReturnType<typeof useDataSourceResponses>;
